/**
 * Comment moderation status vocabulary — the admin's single source of truth for
 * int ↔ enum name ↔ label. The legacy REST comments page works in the ints
 * (`comments.status` is an inverted SMALLINT: PUBLISHED is 0 and anything truthy
 * is hidden from normal members); the GraphQL moderation queue works in the enum
 * names. Both admin surfaces take their labels from this one table, so a comment
 * never reads differently on the two pages.
 *
 * Mirrors Brooklyn `lib/services/comments/{status.js,schema.gql,resolvers.js}`
 * (master 590c82b53, feat(CO-568)) and MUST stay in sync with them:
 *
 * | int | enum           | meaning                                              | label          |
 * |-----|----------------|------------------------------------------------------|----------------|
 * | 0   | PUBLISHED      | live                                                 | Published      |
 * | 1   | UNPUBLISHED    | legacy backlog, or taken down after being live       | Not published  |
 * | 2   | NEEDS_REVIEW   | re-review after publication (member report)          | Needs review   |
 * | 3   | PENDING_REVIEW | created, awaiting AI classification (transient)      | Pending review |
 * | 4   | REJECTED       | human reviewed and denied, never live                | Rejected       |
 * | 5   | AI_REJECTED    | terminal automated denial (reserved, unused in MVP)  | AI rejected    |
 * | 6   | AI_FLAGGED     | AI flagged, awaiting human review (the queue status) | AI flagged     |
 *
 * 3, 4 and 6 are visible to their author and nobody else. PENDING_REVIEW (3) and
 * REJECTED (4) split the two facts that UNPUBLISHED (1) used to conflate:
 * "created, never reviewed" vs "reviewed and denied". Legacy rows are NOT
 * migrated, so 1 keeps its honest ambiguity and is labelled "Not published" for
 * both. AI_FLAGGED (6) is what Brooklyn's CO-568 worker writes when the
 * classifier holds a comment for a human — the status the /moderation page lists.
 *
 * The table is keyed by the `CommentStatus` enum so that a new enum member
 * without a row here fails type-check instead of shipping unlabelled.
 * @module services/comments/status
 */
import { CommentStatus } from '../../graphql-types'

/** One row of the contract: the Brooklyn int, the admin label, and what the status means. */
export interface CommentStatusRow {
  value: number
  label: string
  meaning: string
}

/**
 * The seven-row contract, keyed by enum name and ordered by int. Frozen because
 * both surfaces read it and neither owns it — nothing may rewrite a label or an
 * int at runtime.
 */
export const COMMENT_STATUS_TABLE: Record<CommentStatus, CommentStatusRow> = Object.freeze({
  [CommentStatus.Published]: {
    value: 0,
    label: 'Published',
    meaning: 'Live and visible to everyone',
  },
  [CommentStatus.Unpublished]: {
    value: 1,
    label: 'Not published',
    meaning: 'Legacy backlog, or taken down after having been live',
  },
  [CommentStatus.NeedsReview]: {
    value: 2,
    label: 'Needs review',
    meaning: 'Flagged for re-review after publication (member report)',
  },
  [CommentStatus.PendingReview]: {
    value: 3,
    label: 'Pending review',
    meaning: 'Created, awaiting AI classification — transient, visible only to its author',
  },
  [CommentStatus.Rejected]: {
    value: 4,
    label: 'Rejected',
    meaning: 'Human reviewed and denied, never live, visible only to its author',
  },
  [CommentStatus.AiRejected]: {
    value: 5,
    label: 'AI rejected',
    meaning: 'Terminal automated denial (reserved, unused in the MVP)',
  },
  [CommentStatus.AiFlagged]: {
    value: 6,
    label: 'AI flagged',
    meaning: 'AI flagged, awaiting human review — the moderation queue status, author-visible',
  },
})

// The legacy int constants, read off the table so they cannot drift from the contract.
export const STATUS_PUBLISHED = COMMENT_STATUS_TABLE[CommentStatus.Published].value
export const STATUS_UNPUBLISHED = COMMENT_STATUS_TABLE[CommentStatus.Unpublished].value
export const STATUS_NEEDS_REVIEW = COMMENT_STATUS_TABLE[CommentStatus.NeedsReview].value
export const STATUS_PENDING_REVIEW = COMMENT_STATUS_TABLE[CommentStatus.PendingReview].value
export const STATUS_REJECTED = COMMENT_STATUS_TABLE[CommentStatus.Rejected].value
export const STATUS_AI_REJECTED = COMMENT_STATUS_TABLE[CommentStatus.AiRejected].value
export const STATUS_AI_FLAGGED = COMMENT_STATUS_TABLE[CommentStatus.AiFlagged].value

/**
 * int → label, for the legacy REST page which only ever sees the SMALLINT. Every
 * known status is enumerated so an unrecognised value is reported as unknown
 * rather than silently mislabelled.
 */
export const STATUS_LABELS: Record<number, string> = Object.freeze(
  Object.values(COMMENT_STATUS_TABLE).reduce<Record<number, string>>(
    (labels, row) => ({ ...labels, [row.value]: row.label }),
    {},
  ),
)

/**
 * Get the display label for a comment status int.
 * @param status The Brooklyn `comments.status` int
 * @returns The label, or an explicit unknown marker
 */
export function getStatusLabel (status: number): string {
  return STATUS_LABELS[status] || `Unknown status (${status})`
}

/**
 * Get the enum name for a comment status int — how a legacy-page int is translated
 * into the moderation queue's vocabulary.
 * @param value The Brooklyn `comments.status` int
 * @returns The enum name, or undefined for an int outside the contract
 */
export function getStatusName (value: number): CommentStatus | undefined {
  return Object.values(CommentStatus).find((name) => COMMENT_STATUS_TABLE[name].value === value)
}

/**
 * Get the display label for a comment status enum name — the moderation queue's
 * lookup, guaranteed to agree with `getStatusLabel` for the same row.
 * @param name The GraphQL `CommentStatus` enum name
 * @returns The label
 */
export function getCommentStatusLabel (name: CommentStatus): string {
  return COMMENT_STATUS_TABLE[name].label
}
